import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo } from 'react'
import { Page } from '@/components/layout/page'
import { Skeleton } from '@/components/ui/skeleton'
import { api } from '@/api/http'
import type { GPHomeResponse, FranchiseItem, BucketKey } from '../../gp-home/-components/types'
import { isBDE, BUCKET_KEYS, inr } from '../../gp-home/-components/types'

export const Route = createFileRoute('/mrgolisoda/more/my-franchise/')({
  component: MyFranchisePage,
})

// ─── Helpers ──────────────────────────────────────────────────────────────────

function todayStr() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function bucketSum(franchise: FranchiseItem): { accomplished: number; target: number } {
  let accomplished = 0
  let target = 0
  for (const key of BUCKET_KEYS as readonly BucketKey[]) {
    accomplished += franchise.buckets[key].accomplished
    target += franchise.buckets[key].target
  }
  return { accomplished, target }
}

interface FlatFranchise {
  yolo_customer_id: string
  franchise: string
  employeeName: string
  accomplished: number
  target: number
}

function flatten(data: GPHomeResponse): FlatFranchise[] {
  if (isBDE(data)) {
    return data.franchises.map((f) => {
      const { accomplished, target } = bucketSum(f)
      return { yolo_customer_id: f.yolo_customer_id, franchise: f.franchise, employeeName: '', accomplished, target }
    })
  }
  return data.data.flatMap((emp) =>
    emp.franchises.map((f) => {
      const { accomplished, target } = bucketSum(f)
      return { yolo_customer_id: f.yolo_customer_id, franchise: f.franchise, employeeName: emp.employee_name, accomplished, target }
    }),
  )
}

// ─── Row ──────────────────────────────────────────────────────────────────────

function FranchiseRow({
  item,
  isLast,
  onClick,
}: {
  item: FlatFranchise
  isLast: boolean
  onClick: () => void
}) {
  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '13px 16px',
        borderBottom: isLast ? 'none' : '1px solid #F0F0F0',
        background: '#fff',
        cursor: 'pointer',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: '#1A1A1A' }}>{item.franchise}</div>
        <div style={{ fontSize: 12, color: '#666666', marginTop: 3 }}>
          {item.employeeName ? `${item.employeeName} · ` : ''}
          GP today {inr(item.accomplished)} / {inr(item.target)}
        </div>
      </div>
      <span style={{ color: '#104BFC', fontSize: 18, flexShrink: 0 }}>›</span>
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

function MyFranchisePage() {
  const date = todayStr()
  const navigate = useNavigate()

  const { data, isLoading } = useQuery({
    queryKey: ['gp-home', date, undefined],
    queryFn: () => api.get<GPHomeResponse>(`/field-customization/goli-soda/gp-home/?date=${date}`),
  })

  const franchises = useMemo(() => (data ? flatten(data) : []), [data])

  // If only one franchise, skip list and go directly to detail
  useEffect(() => {
    const f = franchises[0]
    if (franchises.length === 1 && f) {
      navigate({
        to: '/mrgolisoda/more/my-franchise/$franchiseId',
        params: { franchiseId: f.yolo_customer_id },
        search: { name: f.franchise },
        replace: true,
      })
    }
  }, [franchises, navigate])

  function goToFranchise(item: FlatFranchise) {
    navigate({
      to: '/mrgolisoda/more/my-franchise/$franchiseId',
      params: { franchiseId: item.yolo_customer_id },
      search: { name: item.franchise },
    })
  }

  return (
    <Page title="My franchise" back scaled>
      <div style={{ padding: '12px 16px 32px', background: '#F5F5F5', minHeight: '100%' }}>
        <p style={{ fontSize: 13, color: '#808080', marginBottom: 12 }}>Pick a franchise</p>

        {isLoading && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-16 w-full rounded-xl" />)}
          </div>
        )}

        {!isLoading && franchises.length > 1 && (
          <div style={{ background: '#fff', borderRadius: 14, overflow: 'hidden', border: '1px solid #EBEBEB' }}>
            {franchises.map((item, i) => (
              <FranchiseRow
                key={item.yolo_customer_id}
                item={item}
                isLast={i === franchises.length - 1}
                onClick={() => goToFranchise(item)}
              />
            ))}
          </div>
        )}

        {!isLoading && franchises.length === 0 && (
          <p style={{ fontSize: 13, color: '#808080', textAlign: 'center', marginTop: 40 }}>
            No franchises found
          </p>
        )}
      </div>
    </Page>
  )
}
